import { reset, SubmissionError } from 'redux-form';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../../../shared/form-elements/select/index';
import { actions as parentActions } from '../../list';

const defaultInitialState = {
	isShow: false,
	fileToUpload: [],
	disabledFields: []
};

const createThreadsForm = ({
	namespace,
	stateNodeName,
	formName
}) => {
	const AFFILIATES_LOOKUP_SUCCESS = `${namespace}/${stateNodeName}-affiliates-lookup-success`;
	const AFFILIATES_LOOKUP_ERROR = `${namespace}/${stateNodeName}-affiliates-lookup-error`;
	const TOGGLE_CREATE_FORM = `${namespace}/${stateNodeName}-toggle-create-form`;
	const FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;
	const PREPARATION_FILE_SUCCESS = `${namespace}/${stateNodeName}-preparation-file-success`;
	const REMOVE_FILE = `${namespace}/${stateNodeName}-remove-file`;
	const UPLOAD_SUCCESS = `${namespace}/${stateNodeName}-upload-success`;
	const UPLOAD_ERROR = `${namespace}/${stateNodeName}-upload-error`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		formSubmit: formData => (dispatch, getState) => {
			const url = [];
			const data = [];

			const defaultUrl = endpoints.THREADS.LIST;

			const defaultData = {
				...formData,
				title: formData.title || '',
				text: formData.text || '',
				priority: formData.priority && formData.priority[0] && formData.priority[0].value || null,
				affiliate_id: null
			};

			if (formData['affiliate_id']) {
				formData['affiliate_id'].forEach(affiliateEntity => {
					url.push(defaultUrl);
					data.push({
						...defaultData,
						affiliate_id: affiliateEntity.value
					});
				});
			} else {
				url.push(defaultUrl);
				data.push({
					...defaultData
				});
			}

			const request = http({
				url,
				method: 'post',
				actions: [
					FORM_SUBMIT_SUCCESS,
					FORM_SUBMIT_ERROR
				],
				data
			});

			dispatch(parentActions.toggleProgressBar());

			return request(dispatch).then(action => {
				if (action.type === FORM_SUBMIT_SUCCESS) {
					const { filesToUpload } = getState()[`${namespace}`].createThreadForm;

					if (isEmpty(filesToUpload)) {
						dispatch(parentActions.fetchData());
					} else {
						const postIds = action.payload.map(entity => entity.data.posts[0].id);
						dispatch(actions.upload(postIds))
							.then(action => {
								if (action.type === UPLOAD_SUCCESS) {
									dispatch(parentActions.fetchData());
								}
							});
					}
				}

				if (action.type === FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					dispatch(parentActions.toggleProgressBar());

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
		},

		affiliatesLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [
					AFFILIATES_LOOKUP_SUCCESS,
					AFFILIATES_LOOKUP_ERROR
				]
			});

			return request(dispatch).then(action => {
				if (action.type === AFFILIATES_LOOKUP_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},

		toggleCreateThreadForm: (disabledFields) => dispatch => {
			dispatch(reset(formName));
			dispatch({ type: TOGGLE_CREATE_FORM, payload: disabledFields });
		},

		preparationFile: (file, nextAction) => dispatch => {
			nextAction(file.name);

			dispatch({
				type: PREPARATION_FILE_SUCCESS,
				payload: file
			});
		},

		removeFile: index => ({
			type: REMOVE_FILE,
			payload:index
		}),

		upload: postIds => (dispatch, getState) => {
			const data = new FormData();
			const { createThreadForm: { filesToUpload } } = getState()[`${namespace}`];
			const url = [];
			const uploadData = [];

			filesToUpload.forEach((file, index) => {
				data.append(`files[${index}]`, file);
			});

			postIds.forEach(postId => {
				url.push(endpoints.THREADS.UPLOAD.replace('{postId}', postId));
				uploadData.push(data);
			});

			const request = http({
				url,
				method: 'put',
				actions: [
					UPLOAD_SUCCESS,
					UPLOAD_ERROR
				],
				data: uploadData
			});

			return request(dispatch);
		}
	};

	// reducers
	const reducers = {
		[TOGGLE_CREATE_FORM]: (state, action) => ({
			...state,
			createThreadForm: {
				...state.createThreadForm,
				isShow: !state.createThreadForm.isShow,
				filesToUpload: [],
				disabledFields: action.payload
					? state.createThreadForm.disabledFields.concat(action.payload)
					: state.createThreadForm.disabledFields
			}
		}),

		[FORM_SUBMIT_SUCCESS]: state => ({
			...state,
			data: null,
			createThreadForm: {
				...state.createThreadForm,
				isShow: false
			}
		}),

		[PREPARATION_FILE_SUCCESS]: (state, action) => ({
			...state,
			createThreadForm: {
				...state.createThreadForm,
				filesToUpload: [].concat(state.createThreadForm.filesToUpload, action.payload)
			}
		}),

		[REMOVE_FILE]: (state, action) => ({
			...state,
			createThreadForm: {
				...state.createThreadForm,
				filesToUpload: state.createThreadForm.filesToUpload.filter((file, index) => index !== action.payload)
			}
		})
	};

	// selectors
	const selectors = {
		getInitialValues: state => ({
			priority: [{value: 'normal', label: 'normal'}]
		}),
		getPrioritiesList: state => createSelectOptionsFromSysEnums(state.root.lists['thread-priorities']),
		getShouldShowCreateThreadFormStatus: state => state[`${namespace}`].createThreadForm.isShow,
		getAttachmentsInfo: state => {
			const { createThreadForm: { filesToUpload } } = state[`${namespace}`];
			return filesToUpload && filesToUpload.filter(file => file).map(file => file.name);
		},
		getDisabledFields: state => state[`${namespace}`].createThreadForm.disabledFields
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default createThreadsForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/threads/components/create-form/index.js